import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  Spinner,
  Checkbox,
  HStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { CircleAlert, Info, TriangleAlert } from "lucide-react";
import { showApiErrorToast } from "../../../../../utils/showApiErrorToast";
import { Trade, ExecutionTradeOverlay } from "../../../../../types/trade";
import {
  enrichExecutionOverlayFromTrade,
  SL_COLOR,
  TP_COLOR,
  tradeToExecutionOverlay,
} from "../../../../../utils/executionTradeOverlayLevels";
import { buildSimulationChangeMap, mergeSimulationTradeRows, enrichSimulatedOverlayForChart, simulatedSlTpLevels, executionCandleTypeSeconds } from "../../../../../utils/simulationTradeOverlay";
import {
  filterTradesInReviewWindow,
  normalizeInstrumentSymbol,
  overlayClosedExitInWindow,
  rankSymbolsInReviewWindow,
  resolveReviewWindowBounds,
  tradeClosedExitInWindow,
  type ReviewWindowBounds,
  type ReviewWindowMembership,
} from "../../../../../utils/tradeReviewWindow";
import type {
  ExecutionCandle,
  ExecutionCandlesSuggestedPeriod,
} from "../../../../../types/trade";
import { useExecutionCandles } from "../../../../../hooks/useTradeStatistics";
import { useInstrumentResolve } from "../../../../../hooks/useInstrumentResolve";
import { useWizardMarketDataOhlcGate } from "../../../../../hooks/useWizardMarketDataOhlcGate";
import { useActiveBrokerAccountOptional } from "../../../../../hooks/useActiveBrokerAccount";
import { useInstrumentSetup } from "../../../../../contexts/InstrumentSetupContext";
import { useUserNotificationTimezone } from "../../../../../hooks/useUserNotificationTimezone";
import { useHistory } from "react-router-dom";
import { buildConnectBrokerManagementOpenUrl } from "../../../Profile/connectBroker/connectBrokerWizardConstants";
import { InstrumentsApiError, resolveInstrument, setMarketDataPreference } from "../../../../../services/instrumentsApi";
import {
  MarketDataAssetClassRequiredModal,
  MarketDataQuoteVariantModal,
} from "../../../Reports/InstrumentResolveModals";
import { MarketDataUnavailableChartOverlay } from "../../../../../components/MarketData/MarketDataUnavailableChartOverlay";
import { MarketDataAttributionNotice } from "../../../../../components/MarketData/MarketDataAttributionNotice";
import { pickAssetClassFromTrades } from "../../../../../utils/tradeAssetClass";
import { buildMarketDataPreferenceRequest } from "../../../../../utils/marketDataPreferenceSave";
import { EXECUTION_CHART_MAX_BARS } from "../../../../../utils/candleTimeframeBarBudget";
import type { MarketDataSourceOption } from "../../../../../types/marketDataSources";
import { getBacktestSymbols, BacktestsApiError } from "../../../../../services/backtestsApi";
import { getReportSymbols, ReportsApiError } from "../../../../../services/reportsApi";
import ExecutionCandlesChart from "./ExecutionCandlesChart";
import { ExecutionCandlesHistoryLimitBanner } from "./ExecutionCandlesHistoryLimitBanner";
import { TradeReviewChartLegendArrow } from "./TradeReviewChartLegendArrow";
import ExecutionTradesList from "./ExecutionTradesList";
import { PolicyOverlaySummary } from "./PolicyOverlaySummary";
import { SimulationOverlaySummary } from "./SimulationOverlaySummary";
import { Field, FieldLabel } from "@/components/ui/field";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { REGIME_OVERLAY_LEGEND_ITEMS } from "../../../../../utils/marketConditionRegimeColors";
import { ResearchDisclaimerNotice } from "../../../../../components/ResearchDisclaimerNotice";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { EmbeddedLoadingChart } from "@/components/ui/embedded-loading-chart";
import { useTradeReviewThemeColors } from "./tradeReviewTheme";
import {
  TRADE_REVIEW_ALERT_CLASS,
  TRADE_REVIEW_ALERT_DESC_CLASS,
  TRADE_REVIEW_ALERT_TITLE_CLASS,
} from "./tradeReviewAlertUi";
import {
  formatExecutionCandleTypeLabel,
  isExecutionCandleTypeDisabledForBarBudget,
  isExecutionCandleTypeFinerThan,
  executionCandleTypesMatch,
  normalizeExecutionCandleType,
  pickExecutionCandlesBarBudgetFloor,
  pickRequestedExecutionCandleType,
  pickResolvedExecutionCandleType,
} from "../../../../../utils/executionCandleTimeframe";
import {
  getDatabentoRecentTailMessage,
  getExecutionCandlesHistoryLimitMessage,
  getExecutionCandlesPartialMessage,
  getExecutionCandlesSuggestedCandleTypes,
  getUserFacingCandleResolutionReason,
  isExecutionCandlesBackfillIncomplete,
  isExecutionCandlesHistoryLimited,
  isExecutionCandlesTypeCoarsened,
} from "../../../../../utils/executionCandlesStatus";
import {
  buildExecutionCandlesParams,
  EXECUTION_CANDLES_TRADE_IDS_HARD_MAX,
  EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP,
  resolveExecutionCandlesBacktestId,
  warnExecutionCandlesCohortMismatch,
  type ExecutionCandlesCohortIntent,
  type ExecutionCandlesRequestWindow,
} from "../../../../../utils/executionCandlesParams";
import { canFetchProductionExecutionCandles } from "../../../../../utils/executionCandlesFetchGate";
import { marketDataConnectGateFromConnectorRequired } from "../../../../../utils/marketDataConnectGate";
import { resolveTradeReviewChartUnavailablePayload } from "../../../../../utils/marketDataUnavailable";
import { normalizeExecutionCandles } from "@/utils/executionCandlesNormalize";
import {
  expandLocalCandleWindowToCoverResolvedSpan,
  expandLocalCandleWindowToCoverTradeTimes,
  localCandleWindowsEqual,
  mergeLocalCandleWindows,
  type LocalCandleWindow,
} from "../../../../../utils/expandLocalCandleWindowToCoverTrades";
import {
  cohortOutcomeFromOverlay,
  computeCohortPreviewStatsFromOverlays,
  computeOverlayDirectionStats,
  computePolicyOverlayStats,
  filterTradesForCohortPreview,
  filterTradesForPolicyOverlay,
  filterTradesForSimulationOverlay,
  COHORT_PREVIEW_OTHER_COLOR,
  type CohortListFilter,
  type CohortPreviewStats,
  type PolicyListFilter,
  type SimulationListFilter,
} from "../../../../../utils/cohortTradesResolve";
import type { TradeListWindowQuery } from "../../../../../utils/notificationPeriod";
import {
  readTradeReviewSlotState,
  writeTradeReviewSlotState,
} from "../../../../../utils/reportDetailUiPersistence";
import {
  laneBIdsToIncludeOverlays,
  type TesseraLaneBOverlayId,
} from "../../../../../types/executionCandlesOverlays";
import {
  isReportCandleTfDiverged,
  laneBOverlaysFromPickPolicy,
} from "../../../../../utils/vela/laneBFromPickPolicy";
import { readPickListForReport } from "../../../../../utils/reportPickListStorage";

const TRADE_REVIEW_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

export type TradeReviewSymbolDiscovery =
  | { kind: "backtest"; id: string }
  | { kind: "report"; id: string };

export interface TradeReviewTabProps {
  brokerAccountCode: string | null;
  tradesForSymbols: Trade[];
  onOpenTrade?: (tradeId: string) => void;
  showEditColumn?: boolean;
  visualStyle?: "default" | "reports";
  policyOverlay?: {
    kept_trade_ids: string[];
    removed_trade_ids: string[];
  } | null;
  requestWindow?: {
    timeframe?: "today" | "7-days" | "14-days" | "30-days" | "all-time" | string;
    start_date?: string;
    end_date?: string;
    local_start_date?: string;
    local_end_date?: string;
    timezone?: string;
  };
  /**
   * Inclusive as-of instant (e.g. report `created_at`). Caps the review window and
   * closed-exit membership so trades after report generation are excluded.
   */
  asOfEndMs?: number | null;
  /** Mirrors trade log list window (local calendar or timeframe preset). */
  tradeListWindow?: TradeListWindowQuery;
  reportIdForCohort?: string;
  exactTradeIds?: string[];
  cohortIntent?: ExecutionCandlesCohortIntent;
  /**
   * Demo Entry Analysis job — Preview trades uses demo cohort resolve + execution-candles
   * (poll cookie). Do not call production `/reports/...` or `/trades/execution-candles`.
   */
  demoJobId?: string;
  /** Seed chart symbol when trade list is empty (demo overlays arrive from candles). */
  forceChartSymbol?: string;
  /** Override notification timezone (e.g. trade log reporting TZ). */
  reportingTimeZoneOverride?: string;
  symbolDiscovery?: TradeReviewSymbolDiscovery;
  defaultCandleTimeframe?: "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";
  beforeCandleTypeControls?: ReactNode;
  /** Optional: IANA timezone + UTC offset (e.g. from report/backtest run). */
  recordedTimezoneLabel?: string | null;
  /** Hide the built-in title row when a parent card already provides section chrome. */
  suppressHeader?: boolean;
  /** From instrument resolve — reduces ambiguous symbol errors on execution-candles. */
  asset_class?: string;
  /** Broker slug when known (e.g. hyperliquid) — improves MD routing on execution-candles. */
  broker_name?: string | null;
  /** Linked backtest for TM simulation overlay on execution-candles. */
  backtestId?: string;
  /** e.g. `baseline_vs_simulated` — changes overlay legend/copy from kept/removed. */
  simulationOverlayMode?: string;
  /** Nested TM rows from saved backtest / comparison run (authoritative for changed vs unchanged). */
  backtestSimulationRows?: Array<Record<string, unknown>>;
  /** Exit Placement: legs replayed — gates simulated SL/TP on chart. */
  simulatedLegs?: Array<"sl" | "tp">;
  /** Reports: allow collapsing the trades table under the chart. */
  collapsibleTradesList?: boolean;
  /** With `reportIdForCohort`, persist chart UI in sessionStorage across report tab switches. */
  uiPersistenceKey?: string;
  /** Resolved bucket/slice trade IDs — visual highlight only (full report cohort on chart). */
  cohortHighlightIds?: string[];
  /** Enables market bucket preview overlay + list filters. */
  cohortPreviewMode?: boolean;
  /** List filter when `cohortPreviewMode` is active. */
  cohortListFilter?: CohortListFilter;
  /** Overlay-derived cohort stats for the summary strip above the chart. */
  onCohortPreviewStats?: (stats: CohortPreviewStats | null) => void;
  /**
   * Cohort / pick bucket key (e.g. `ma50:above`) — auto-enables locked Lane B overlays.
   * When set with a report candle TF, chart TF is locked to the report TF.
   */
  cohortBucketKey?: string | null;
  /** Lock chart candle TF to `defaultCandleTimeframe` (report / pick / sim). */
  lockReportCandleTimeframe?: boolean;
}

function defaultCandleTypeFromReportDefault(
  defaultCandleTimeframe?: TradeReviewTabProps["defaultCandleTimeframe"]
): string {
  switch (defaultCandleTimeframe) {
    case "1m":
      return "m";
    case "1h":
      return "h";
    case "5m":
    case "15m":
    case "30m":
    case "4h":
      return defaultCandleTimeframe;
    default:
      return "h";
  }
}

function readPersistedTradeReviewSlot(
  reportIdForCohort: string | undefined,
  uiPersistenceKey: string | undefined
) {
  if (!reportIdForCohort?.trim() || !uiPersistenceKey?.trim()) return null;
  return readTradeReviewSlotState(reportIdForCohort, uiPersistenceKey);
}

function normalizePolicyTradeId(id: string | null | undefined): string {
  return String(id ?? "").trim().toLowerCase();
}

function toYmd(date: Date | null | undefined): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatLocalDateTime(value: string | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

function formatPeriodChipLabel(startIso: string, endIso: string): string {
  const start = formatLocalDateTime(startIso);
  const end = formatLocalDateTime(endIso);
  const startDay = start.split(",")[0]?.trim() ?? start;
  const endDay = end.split(",")[0]?.trim() ?? end;
  if (startDay === endDay) return startDay;
  return `${startDay} – ${endDay}`;
}

function periodKey(p: Pick<ExecutionCandlesSuggestedPeriod, "window_start" | "window_end">): string {
  return `${p.window_start}\0${p.window_end}`;
}

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
/** Fallback when API omits `max_bars` (matches `EXECUTION_CANDLES_MAX_BARS` default). */
const DEFAULT_EXECUTION_CANDLES_MAX_BARS = EXECUTION_CHART_MAX_BARS;

const candleTypeOptions: Array<{ value: string; label: string }> = [
  { value: "m", label: "1m" },
  { value: "5m", label: "5m" },
  { value: "15m", label: "15m" },
  { value: "30m", label: "30m" },
  { value: "h", label: "1h" },
  { value: "4h", label: "4h" },
];

const TradeReviewTab: React.FC<TradeReviewTabProps> = ({
  brokerAccountCode,
  tradesForSymbols,
  requestWindow,
  asOfEndMs = null,
  tradeListWindow,
  reportIdForCohort,
  exactTradeIds: exactTradeIdsProp,
  cohortIntent,
  demoJobId: demoJobIdProp,
  forceChartSymbol,
  reportingTimeZoneOverride,
  onOpenTrade,
  showEditColumn = true,
  visualStyle = "default",
  policyOverlay,
  symbolDiscovery,
  defaultCandleTimeframe,
  beforeCandleTypeControls,
  suppressHeader = false,
  asset_class: assetClassProp,
  broker_name: brokerNameProp,
  backtestId,
  simulationOverlayMode,
  backtestSimulationRows,
  simulatedLegs,
  collapsibleTradesList = false,
  uiPersistenceKey,
  cohortHighlightIds,
  cohortPreviewMode = false,
  cohortListFilter = "cohort",
  onCohortPreviewStats,
  cohortBucketKey = null,
  lockReportCandleTimeframe = false,
}) => {
  const persistedUi = readPersistedTradeReviewSlot(reportIdForCohort, uiPersistenceKey);
  const isBaselineVsSimulatedOverlay = simulationOverlayMode === "baseline_vs_simulated";
  /** Demo Entry Analysis: candle TF is fixed to the report choice — no Trade Review switching. */
  const demoCandleTimeframeLocked = Boolean(demoJobIdProp?.trim());
  const reportCandleTypeLocked =
    lockReportCandleTimeframe ||
    demoCandleTimeframeLocked ||
    Boolean(cohortPreviewMode && defaultCandleTimeframe);
  const lockedDemoCandleType = reportCandleTypeLocked
    ? defaultCandleTypeFromReportDefault(defaultCandleTimeframe)
    : null;
  const brokerCtx = useActiveBrokerAccountOptional();
  const tradeAccounts = brokerCtx?.accounts ?? [];
  const { openSetup, isOpen: instrumentSetupOpen } = useInstrumentSetup();
  const effectiveBrokerAccountCode =
    brokerAccountCode?.trim() || brokerCtx?.activeAccountCode?.trim() || "";
  const history = useHistory();
  const instrumentResolve = useInstrumentResolve(brokerAccountCode ?? "");
  const resolveInstrumentForChartRef = useRef(instrumentResolve.resolve);
  resolveInstrumentForChartRef.current = instrumentResolve.resolve;
  const lastInstrumentResolveKeyRef = useRef<string | null>(null);
  const { timezone: notificationTimezone } = useUserNotificationTimezone();
  const reportingTimeZone =
    reportingTimeZoneOverride?.trim() ||
    notificationTimezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "UTC";

  const {
    isReportsEmbed,
    cardBg,
    borderColor,
    secondaryTextColor,
    inputBg,
  } = useTradeReviewThemeColors(visualStyle);

  const [apiSymbols, setApiSymbols] = useState<string[] | undefined>(undefined);
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState<string | null>(null);

  const [chartActiveSymbol, setChartActiveSymbol] = useState<string>(
    () => persistedUi?.chartActiveSymbol ?? ""
  );

  const ohlcSetupGate = useWizardMarketDataOhlcGate({
    brokerAccountCode: effectiveBrokerAccountCode,
    symbol: chartActiveSymbol || undefined,
    enabled:
      Boolean(effectiveBrokerAccountCode) &&
      Boolean(chartActiveSymbol.trim()) &&
      !demoJobIdProp?.trim(),
  });

  const [includeDust, setIncludeDust] = useState(false);
  const [tradesListOpen, setTradesListOpen] = useState(
    () => persistedUi?.tradesListOpen ?? true
  );
  /** Explicit chunk from BE `suggested_periods` (null = default / is_current slice). */
  const [periodWindow, setPeriodWindow] = useState<{ start: string; end: string } | null>(null);
  const [cachedSuggestedPeriods, setCachedSuggestedPeriods] = useState<ExecutionCandlesSuggestedPeriod[]>(
    []
  );
  /** Minimum TF allowed for current window after last successful candles load (bar-budget floor). */
  const [barBudgetMinCandleType, setBarBudgetMinCandleType] = useState<string | null>(null);
  /** Sticky coarsening copy — survives the refetch triggered by syncing candle type to resolved TF. */
  const [coarseningNotice, setCoarseningNotice] = useState<{
    requestedLabel: string;
    resolvedLabel: string;
    reason?: string;
  } | null>(null);
  const coarseningNoticeBoundKeyRef = useRef<string | null>(null);
  const [policyListFilter, setPolicyListFilter] = useState<PolicyListFilter>("all");
  const [simulationListFilter, setSimulationListFilter] =
    useState<SimulationListFilter>("all");
  const [date, setDate] = useState<string>(
    () => persistedUi?.date ?? toYmd(new Date())
  );
  const [candleType, setCandleType] = useState<string>(() =>
    lockedDemoCandleType ??
    persistedUi?.candleType ??
    defaultCandleTypeFromReportDefault(defaultCandleTimeframe)
  );
  const [selectedTradeId, setSelectedTradeId] = useState<string | null>(
    () => persistedUi?.selectedTradeId ?? null
  );
  const [enabledLaneBOverlays, setEnabledLaneBOverlays] = useState<TesseraLaneBOverlayId[]>(
    () => ["regime"]
  );
  const [focusTradeId, setFocusTradeId] = useState<string | null>(null);
  const [scrollToTradeId, setScrollToTradeId] = useState<string | null>(null);
  const [chartHeightPx, setChartHeightPx] = useState(640);

  useEffect(() => {
    if (!reportIdForCohort?.trim() || !uiPersistenceKey?.trim()) return;
    const timer = window.setTimeout(() => {
      writeTradeReviewSlotState(reportIdForCohort, uiPersistenceKey, {
        chartActiveSymbol,
        selectedTradeId,
        candleType,
        date,
        showPnlBackgroundOverlay: true,
        showRegimeOverlay: true,
        tradesListOpen,
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    reportIdForCohort,
    uiPersistenceKey,
    chartActiveSymbol,
    selectedTradeId,
    candleType,
    date,
    tradesListOpen,
  ]);

  useEffect(() => {
    if (!symbolDiscovery) {
      setApiSymbols(undefined);
      setSymbolsError(null);
      setSymbolsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setSymbolsLoading(true);
      setSymbolsError(null);
      try {
        const resp =
          symbolDiscovery.kind === "backtest"
            ? await getBacktestSymbols(symbolDiscovery.id)
            : await getReportSymbols(symbolDiscovery.id);
        if (cancelled) return;
        const list = Array.isArray(resp.symbols) ? resp.symbols.map((s) => String(s).trim()).filter(Boolean) : [];
        setApiSymbols(list);
      } catch (e) {
        if (cancelled) return;
        setApiSymbols([]);
        const msg =
          e instanceof BacktestsApiError || e instanceof ReportsApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : "Failed to load symbols.";
        setSymbolsError(msg);
      } finally {
        if (!cancelled) setSymbolsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [symbolDiscovery?.kind, symbolDiscovery?.id]);

  useEffect(() => {
    if (reportCandleTypeLocked) {
      const locked = defaultCandleTypeFromReportDefault(defaultCandleTimeframe);
      setCandleType((prev) => (prev === locked ? prev : locked));
      return;
    }
    if (!defaultCandleTimeframe) return;
    const next = defaultCandleTypeFromReportDefault(defaultCandleTimeframe);
    setCandleType((prev) => (prev === next ? prev : next));
  }, [defaultCandleTimeframe, reportCandleTypeLocked]);

  const windowMode = useMemo(() => {
    if (tradeListWindow) return "trade_list" as const;
    if (requestWindow?.local_start_date && requestWindow?.local_end_date) return "local" as const;
    if (requestWindow?.start_date && requestWindow?.end_date) return "range" as const;
    if (requestWindow?.timeframe) return "timeframe" as const;
    return "date" as const;
  }, [requestWindow, tradeListWindow]);

  const useReportClosedExitCohort =
    isReportsEmbed || Boolean(reportIdForCohort?.trim());
  const reviewMembership: ReviewWindowMembership = useReportClosedExitCohort
    ? "closed_exit"
    : "overlap";

  const reviewBounds: ReviewWindowBounds | null = useMemo(() => {
    let bounds: ReviewWindowBounds | null = null;
    if (windowMode === "date") {
      bounds = resolveReviewWindowBounds("date", { dateYmd: date, reportingTimeZone });
    } else if (windowMode === "local" && requestWindow?.local_start_date && requestWindow?.local_end_date) {
      bounds = resolveReviewWindowBounds("range", {
        start_date: `${requestWindow.local_start_date}T00:00:00`,
        end_date: `${requestWindow.local_end_date}T23:59:59`,
        reportingTimeZone: requestWindow.timezone ?? reportingTimeZone,
      });
    } else if (windowMode === "range") {
      bounds = resolveReviewWindowBounds("range", {
        start_date: requestWindow?.start_date,
        end_date: requestWindow?.end_date,
        reportingTimeZone,
      });
    } else if (windowMode === "trade_list" && tradeListWindow?.kind === "local") {
      const p = tradeListWindow.period;
      bounds = resolveReviewWindowBounds("range", {
        start_date: `${p.local_start_date}T00:00:00`,
        end_date: `${p.local_end_date}T23:59:59`,
        reportingTimeZone: p.timezone ?? reportingTimeZone,
      });
    } else {
      bounds = resolveReviewWindowBounds("timeframe", {
        timeframe:
          windowMode === "trade_list" && tradeListWindow?.kind === "timeframe"
            ? tradeListWindow.timeframe
            : requestWindow?.timeframe,
        reportingTimeZone,
      });
    }
    if (
      bounds &&
      asOfEndMs != null &&
      Number.isFinite(asOfEndMs) &&
      bounds.endMs > asOfEndMs
    ) {
      return { ...bounds, endMs: asOfEndMs };
    }
    return bounds;
  }, [windowMode, date, requestWindow, tradeListWindow, reportingTimeZone, asOfEndMs]);

  const rankedSymbols = useMemo(() => {
    if (!reviewBounds) return [];
    return rankSymbolsInReviewWindow(tradesForSymbols, reviewBounds, includeDust, reviewMembership);
  }, [tradesForSymbols, reviewBounds, includeDust, reviewMembership]);

  const chartSymbolTrades = useMemo(() => {
    if (!reviewBounds || !chartActiveSymbol) return [] as Trade[];
    return filterTradesInReviewWindow(tradesForSymbols, reviewBounds, {
      includeDust,
      symbol: chartActiveSymbol,
      membership: reviewMembership,
    });
  }, [tradesForSymbols, reviewBounds, chartActiveSymbol, includeDust, reviewMembership]);

  const instrumentOptions = useMemo(() => {
    let options = rankedSymbols;
    if (symbolDiscovery && apiSymbols && apiSymbols.length > 0) {
      const allowed = new Set(apiSymbols.map((s) => normalizeInstrumentSymbol(s)));
      options = options.filter((o) => allowed.has(normalizeInstrumentSymbol(o.symbol)));
      if (options.length === 0) {
        options = apiSymbols.map((symbol) => ({ symbol, count: 0 }));
      }
    }
    const forced = forceChartSymbol?.trim();
    if (forced && !options.some((o) => normalizeInstrumentSymbol(o.symbol) === normalizeInstrumentSymbol(forced))) {
      options = [{ symbol: forced, count: 0 }, ...options];
    }
    return options;
  }, [rankedSymbols, symbolDiscovery, apiSymbols, forceChartSymbol]);

  const instrumentOptionsKey = useMemo(
    () => instrumentOptions.map((o) => `${o.symbol}:${o.count}`).join("\0"),
    [instrumentOptions]
  );

  useEffect(() => {
    if (instrumentOptions.length === 0) {
      const forced = forceChartSymbol?.trim();
      setChartActiveSymbol(forced || "");
      return;
    }
    setChartActiveSymbol((prev) => {
      if (prev && instrumentOptions.some((o) => o.symbol === prev)) return prev;
      return instrumentOptions[0].symbol;
    });
  }, [instrumentOptionsKey, instrumentOptions, forceChartSymbol]);

  useEffect(() => {
    setPeriodWindow(null);
    setCachedSuggestedPeriods([]);
    setSelectedTradeId(null);
    setFocusTradeId(null);
    setScrollToTradeId(null);
  }, [
    chartActiveSymbol,
    date,
    reviewBounds?.startMs,
    reviewBounds?.endMs,
    includeDust,
    requestWindow?.timeframe,
    requestWindow?.start_date,
    requestWindow?.end_date,
  ]);

  const isAllTime =
    (windowMode === "timeframe" && requestWindow?.timeframe === "all-time") ||
    (windowMode === "trade_list" &&
      tradeListWindow?.kind === "timeframe" &&
      tradeListWindow.timeframe === "all-time");

  const executionCandlesRequestWindow = useMemo((): ExecutionCandlesRequestWindow | null => {
    if (!chartActiveSymbol) return null;

    if (periodWindow) {
      return {
        kind: "range",
        start_date: periodWindow.start,
        end_date: periodWindow.end,
        reportingTimeZone,
      };
    }

    if (tradeListWindow) {
      return { kind: "trade_list", window: tradeListWindow };
    }

    if (windowMode === "date" && date) {
      return { kind: "date", date, reportingTimeZone };
    }
    if (
      windowMode === "local" &&
      requestWindow?.local_start_date &&
      requestWindow?.local_end_date
    ) {
      return {
        kind: "local",
        period: {
          local_start_date: requestWindow.local_start_date,
          local_end_date: requestWindow.local_end_date,
          timezone: requestWindow.timezone ?? reportingTimeZone,
        },
      };
    }
    if (windowMode === "range" && requestWindow?.start_date && requestWindow?.end_date) {
      return {
        kind: "range",
        start_date: requestWindow.start_date,
        end_date: requestWindow.end_date,
        reportingTimeZone,
      };
    }
    if (windowMode === "timeframe" && requestWindow?.timeframe) {
      return { kind: "timeframe", timeframe: requestWindow.timeframe, reportingTimeZone };
    }
    return null;
  }, [
    chartActiveSymbol,
    periodWindow,
    tradeListWindow,
    windowMode,
    date,
    requestWindow,
    reportingTimeZone,
  ]);

  const activeSymbolKey = useMemo(
    () => (chartActiveSymbol ? normalizeInstrumentSymbol(chartActiveSymbol) : ""),
    [chartActiveSymbol]
  );

  /** Trades for the active symbol — used to resolve overlays / policy IDs (cohort membership enforced separately). */
  const activeSymbolTrades = useMemo(() => {
    if (!activeSymbolKey) return [] as Trade[];
    return tradesForSymbols.filter(
      (t) => normalizeInstrumentSymbol(t.symbol) === activeSymbolKey
    );
  }, [tradesForSymbols, activeSymbolKey]);

  const regimePolicyOverlayActive = Boolean(
    policyOverlay &&
      ((policyOverlay.kept_trade_ids?.length ?? 0) > 0 ||
        (policyOverlay.removed_trade_ids?.length ?? 0) > 0) &&
      !isBaselineVsSimulatedOverlay
  );

  /**
   * Candles cohort UUID — never replace with a mega trade_ids CSV.
   * Regime kept/removed is tagged client-side from policyOverlay after fetch.
   * See {@link resolveExecutionCandlesBacktestId}.
   */
  const backtestIdForCandles = useMemo(
    () =>
      resolveExecutionCandlesBacktestId({
        backtestId,
        reportIdForCohort,
        cohortIntent,
        regimePolicyOverlayActive,
        isBaselineVsSimulatedOverlay,
      }),
    [
      backtestId,
      cohortIntent,
      regimePolicyOverlayActive,
      reportIdForCohort,
      isBaselineVsSimulatedOverlay,
    ]
  );

  const tradeTimesForCandleWindow = useMemo(() => {
    const times: Array<string | Date | null | undefined> = [];
    for (const t of activeSymbolTrades) {
      times.push(t.entryDate, t.exitDate ?? null);
    }
    return times;
  }, [activeSymbolTrades]);

  const [spanExpandedLocalWindow, setSpanExpandedLocalWindow] = useState<LocalCandleWindow | null>(
    null
  );

  useEffect(() => {
    setSpanExpandedLocalWindow(null);
  }, [executionCandlesRequestWindow, chartActiveSymbol, brokerAccountCode]);

  const baseLocalCandleWindow = useMemo((): LocalCandleWindow | null => {
    if (!executionCandlesRequestWindow) return null;
    if (executionCandlesRequestWindow.kind === "local") {
      return {
        local_start_date: executionCandlesRequestWindow.period.local_start_date,
        local_end_date: executionCandlesRequestWindow.period.local_end_date,
        timezone: executionCandlesRequestWindow.period.timezone ?? reportingTimeZone,
      };
    }
    if (
      executionCandlesRequestWindow.kind === "trade_list" &&
      executionCandlesRequestWindow.window.kind === "local"
    ) {
      return {
        local_start_date: executionCandlesRequestWindow.window.period.local_start_date,
        local_end_date: executionCandlesRequestWindow.window.period.local_end_date,
        timezone:
          executionCandlesRequestWindow.window.period.timezone ?? reportingTimeZone,
      };
    }
    return null;
  }, [executionCandlesRequestWindow, reportingTimeZone]);

  /** Trade Log date presets — keep OHLC aligned with the picker, not overlap-trade entry spans. */
  const respectUserLocalPeriodForCandles = useMemo(() => {
    if (periodWindow) return false;
    if (cohortIntent !== "trade_log") return false;
    return baseLocalCandleWindow != null;
  }, [periodWindow, cohortIntent, baseLocalCandleWindow]);

  const effectiveLocalCandleWindow = useMemo((): LocalCandleWindow | null => {
    if (!baseLocalCandleWindow) return null;
    if (respectUserLocalPeriodForCandles) {
      return baseLocalCandleWindow;
    }
    let period = expandLocalCandleWindowToCoverTradeTimes(
      baseLocalCandleWindow,
      tradeTimesForCandleWindow
    );
    if (spanExpandedLocalWindow) {
      period = mergeLocalCandleWindows(period, spanExpandedLocalWindow);
    }
    return period;
  }, [
    baseLocalCandleWindow,
    tradeTimesForCandleWindow,
    spanExpandedLocalWindow,
    respectUserLocalPeriodForCandles,
  ]);

  const candleRequestWindow = useMemo((): ExecutionCandlesRequestWindow | null => {
    if (!executionCandlesRequestWindow) return null;
    if (!effectiveLocalCandleWindow) return executionCandlesRequestWindow;
    if (executionCandlesRequestWindow.kind === "local") {
      return {
        kind: "local",
        period: {
          ...executionCandlesRequestWindow.period,
          local_start_date: effectiveLocalCandleWindow.local_start_date,
          local_end_date: effectiveLocalCandleWindow.local_end_date,
          timezone: effectiveLocalCandleWindow.timezone,
        },
      };
    }
    if (
      executionCandlesRequestWindow.kind === "trade_list" &&
      executionCandlesRequestWindow.window.kind === "local"
    ) {
      return {
        kind: "trade_list",
        window: {
          kind: "local",
          period: {
            ...executionCandlesRequestWindow.window.period,
            local_start_date: effectiveLocalCandleWindow.local_start_date,
            local_end_date: effectiveLocalCandleWindow.local_end_date,
            timezone: effectiveLocalCandleWindow.timezone,
          },
        },
      };
    }
    return executionCandlesRequestWindow;
  }, [executionCandlesRequestWindow, effectiveLocalCandleWindow]);

  const exactTradeIdsStableKey = (exactTradeIdsProp ?? [])
    .map((id) => String(id ?? "").trim())
    .filter(Boolean)
    .join("\0");

  const exactTradeIdsForApi = useMemo(() => {
    // Never serialize kept/removed cohorts into trade_ids — use backtest_id / report_id.
    if (backtestIdForCandles) {
      return undefined;
    }
    const ids = exactTradeIdsStableKey
      ? exactTradeIdsStableKey.split("\0")
      : [];
    const demoJobId = demoJobIdProp?.trim();
    if (demoJobId) {
      if (ids.length === 0) return undefined;
      if (ids.length <= EXECUTION_CANDLES_TRADE_IDS_HARD_MAX) return ids;
      return ids.slice(0, EXECUTION_CANDLES_TRADE_IDS_HARD_MAX);
    }
    if (reportIdForCohort?.trim()) {
      // Tiny explicit subsets only; large prop lists fall through to report_id.
      if (ids.length > 0 && ids.length <= EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP) return ids;
      return undefined;
    }
    if (ids.length > 0 && ids.length <= EXECUTION_CANDLES_TRADE_IDS_SOFT_CAP) return ids;
    // Do not expand policyOverlay.kept_trade_ids into the URL (Safari URL length / BE 2000 cap).
    return undefined;
  }, [exactTradeIdsStableKey, reportIdForCohort, backtestIdForCandles, demoJobIdProp]);

  const effectiveCohortIntent = useMemo((): ExecutionCandlesCohortIntent | undefined => {
    if (cohortIntent) return cohortIntent;
    if (backtestIdForCandles) return "backtest";
    if (exactTradeIdsForApi?.length) return "policy_exact";
    if (reportIdForCohort) return visualStyle === "reports" ? "report" : "backtest";
    return visualStyle === "default" ? "trade_log" : undefined;
  }, [cohortIntent, backtestIdForCandles, exactTradeIdsForApi, reportIdForCohort, visualStyle]);

  const brokerNameForAccount = useMemo(() => {
    if (brokerNameProp?.trim()) return brokerNameProp.trim().toLowerCase();
    if (!brokerAccountCode) return null;
    const match = tradeAccounts.find((a) => a.accountCode === brokerAccountCode);
    return match?.brokerName?.trim().toLowerCase() || null;
  }, [brokerAccountCode, brokerNameProp, tradeAccounts]);

  const assetClassFromTrades = useMemo(
    () => pickAssetClassFromTrades(chartSymbolTrades, null),
    [chartSymbolTrades]
  );

  const assetClassHint = assetClassProp ?? assetClassFromTrades;

  const [confirmedAssetClassBySymbol, setConfirmedAssetClassBySymbol] = useState<
    Record<string, string>
  >({});
  const [mdPreferenceSaving, setMdPreferenceSaving] = useState(false);

  const effectiveAssetClass =
    confirmedAssetClassBySymbol[activeSymbolKey] ??
    (instrumentResolve.state.kind === "resolved"
      ? instrumentResolve.state.result.asset_class
      : undefined) ??
    assetClassHint ??
    undefined;

  const productionCandlesFetchAllowed = useMemo(() => {
    if (demoJobIdProp?.trim()) return true;
    return canFetchProductionExecutionCandles({
      state: instrumentResolve.state,
      pendingMarketDataSourceChoice: instrumentResolve.pendingMarketDataSourceChoice,
      pendingQuoteVariantChoice: instrumentResolve.pendingQuoteVariantChoice,
      pendingConsent: instrumentResolve.pendingConsent,
      pendingConnectorRequired: instrumentResolve.pendingConnectorRequired,
      pendingAssetClassPayload: instrumentResolve.pendingAssetClassPayload,
    });
  }, [
    demoJobIdProp,
    instrumentResolve.pendingAssetClassPayload,
    instrumentResolve.pendingConnectorRequired,
    instrumentResolve.pendingConsent,
    instrumentResolve.pendingMarketDataSourceChoice,
    instrumentResolve.pendingQuoteVariantChoice,
    instrumentResolve.state,
  ]);

  const executionCandlesEnabled = Boolean(
    chartActiveSymbol &&
      productionCandlesFetchAllowed &&
      (demoJobIdProp?.trim()
        ? (exactTradeIdsForApi?.length ?? 0) > 0
        : brokerAccountCode && executionCandlesRequestWindow)
  );

  // Derive fetch overlays from stable policy/bucket inputs — not from enabledLaneBOverlays
  // state (that updates in an effect and was re-issuing execution-candles on report preview).
  const autoLaneBIds = useMemo(() => {
    const picks =
      reportIdForCohort?.trim() ? readPickListForReport(reportIdForCohort) : [];
    return laneBOverlaysFromPickPolicy({
      picks,
      cohortBucketKey: cohortBucketKey ?? (cohortPreviewMode ? uiPersistenceKey : null),
      labels: policyOverlay ? ["regime"] : undefined,
    });
  }, [
    reportIdForCohort,
    cohortBucketKey,
    cohortPreviewMode,
    uiPersistenceKey,
    policyOverlay,
  ]);

  const overlayKindsForFetch = useMemo(() => {
    const laneIds = new Set<TesseraLaneBOverlayId>(autoLaneBIds);
    laneIds.add("regime");
    return laneBIdsToIncludeOverlays(Array.from(laneIds));
  }, [autoLaneBIds]);

  const candlesParams = useMemo(() => {
    if (!executionCandlesEnabled) return null;
    try {
      return buildExecutionCandlesParams({
        symbol: chartActiveSymbol,
        brokerAccountCode: brokerAccountCode || "demo",
        brokerName: brokerNameForAccount,
        candleType,
        asset_class: effectiveAssetClass,
        periodWindow,
        requestWindow: candleRequestWindow,
        reportIdForCohort,
        demoJobId: demoJobIdProp,
        exactTradeIds: exactTradeIdsForApi,
        focusTradeId,
        backtestId: backtestIdForCandles,
        cohortIntent: effectiveCohortIntent,
        includeRegimeOverlay: true,
        includeOverlays: overlayKindsForFetch.length
          ? overlayKindsForFetch
          : undefined,
      });
    } catch (err) {
      console.error(err);
      return null;
    }
  }, [
    backtestIdForCandles,
    brokerAccountCode,
    brokerNameForAccount,
    chartActiveSymbol,
    candleType,
    demoJobIdProp,
    effectiveAssetClass,
    effectiveCohortIntent,
    exactTradeIdsForApi,
    executionCandlesEnabled,
    executionCandlesRequestWindow,
    candleRequestWindow,
    overlayKindsForFetch,
    periodWindow,
    reportIdForCohort,
    focusTradeId,
  ]);

  const handleMarketDataUnresolved = useCallback(
    (detail: { symbol?: string; suggested_asset_class?: string }) => {
      const code = effectiveBrokerAccountCode;
      if (!code) return;
      const symbol = detail.symbol?.trim() || chartActiveSymbol;
      if (!symbol) {
        void openSetup(code, { mandatory: false });
        return;
      }
      void resolveInstrumentForChartRef.current(symbol, detail.suggested_asset_class);
    },
    [chartActiveSymbol, effectiveBrokerAccountCode, openSetup]
  );

  useEffect(() => {
    lastInstrumentResolveKeyRef.current = null;
  }, [brokerAccountCode, chartActiveSymbol]);

  const resolveConnectorGate = useMemo(
    () => marketDataConnectGateFromConnectorRequired(instrumentResolve.pendingConnectorRequired),
    [instrumentResolve.pendingConnectorRequired]
  );

  const {
    data,
    loading,
    backfillLoading,
    backfillProgress,
    overlayRefreshing,
    error,
    assetClassRequired,
    dismissAssetClassRequired,
    connectorRequired,
    dismissConnectorRequired,
    brokerConsentRepair,
    dismissBrokerConsentRepair,
    marketDataUnavailable,
    refetch: refetchExecutionCandles,
  } = useExecutionCandles(candlesParams, {
    softLoading: Boolean(
      backtestId?.trim() &&
        (isBaselineVsSimulatedOverlay || Boolean(reportIdForCohort))
    ),
    onMarketDataUnresolved: handleMarketDataUnresolved,
  });

  const instrumentSetupWasOpenRef = useRef(false);
  useEffect(() => {
    if (instrumentSetupWasOpenRef.current && !instrumentSetupOpen) {
      instrumentResolve.reset();
      refetchExecutionCandles();
    }
    instrumentSetupWasOpenRef.current = instrumentSetupOpen;
  }, [instrumentSetupOpen, instrumentResolve, refetchExecutionCandles]);

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ action?: string; accountCode?: string }>).detail;
      if (detail?.action !== "marketDataPreference") return;
      const code = effectiveBrokerAccountCode?.trim();
      if (detail.accountCode && code && detail.accountCode !== code) return;
      instrumentResolve.reset();
      refetchExecutionCandles();
    };
    window.addEventListener("tradesUpdated", handleTradesUpdated);
    return () => window.removeEventListener("tradesUpdated", handleTradesUpdated);
  }, [effectiveBrokerAccountCode, instrumentResolve, refetchExecutionCandles]);

  const resolvedDataCandleType = useMemo(
    () => (data ? pickResolvedExecutionCandleType(data) : null),
    [data]
  );

  const candleTypeMismatch = Boolean(
    resolvedDataCandleType &&
      !executionCandleTypesMatch(candleType, resolvedDataCandleType) &&
      !(data && isExecutionCandlesTypeCoarsened(data, candleType))
  );

  const chartUnavailablePayload = useMemo(
    () =>
      resolveTradeReviewChartUnavailablePayload({
        marketDataUnavailable,
        proactiveConnectGate: ohlcSetupGate.connectGate,
        connectorRequired,
        resolveConnectGate: resolveConnectorGate,
      }),
    [
      marketDataUnavailable,
      ohlcSetupGate.connectGate,
      connectorRequired,
      resolveConnectorGate,
    ]
  );

  const chartOhlcBlocked = Boolean(chartUnavailablePayload);

  /** True while instrument resolve POST is in flight. */
  const instrumentResolvePending =
    Boolean(chartActiveSymbol && brokerAccountCode && !demoJobIdProp?.trim()) &&
    instrumentResolve.state.kind === "loading";
  const chartDataPending =
    executionCandlesEnabled &&
    !chartUnavailablePayload &&
    (loading ||
      overlayRefreshing ||
      backfillLoading ||
      candleTypeMismatch ||
      (!data && !error));
  const chartShellPending =
    instrumentResolvePending ||
    (ohlcSetupGate.scanning && !chartUnavailablePayload) ||
    chartDataPending;

  const mdAssetClassModalOpen = Boolean(assetClassRequired);

  useEffect(() => {
    if (data?.trades_cohort_axis) {
      warnExecutionCandlesCohortMismatch(effectiveCohortIntent, data.trades_cohort_axis);
    }
  }, [data?.trades_cohort_axis, effectiveCohortIntent]);

  useEffect(() => {
    setConfirmedAssetClassBySymbol((prev) => {
      if (!activeSymbolKey || !(activeSymbolKey in prev)) return prev;
      const next = { ...prev };
      delete next[activeSymbolKey];
      return next;
    });
    dismissAssetClassRequired();
  }, [
    activeSymbolKey,
    periodWindow?.start,
    periodWindow?.end,
    executionCandlesRequestWindow,
    dismissAssetClassRequired,
  ]);

  const handleMarketDataAssetClassConfirm = useCallback(
    async (selection: {
      assetClass: string;
      marketDataBroker?: string;
      marketDataSource?: MarketDataSourceOption;
    }) => {
      if (!brokerAccountCode || !chartActiveSymbol || !activeSymbolKey) return;
      setMdPreferenceSaving(true);
      try {
        let resolveContext = null;
        try {
          resolveContext = await resolveInstrument({
            symbol: chartActiveSymbol,
            asset_class: selection.assetClass,
            broker_account_code: brokerAccountCode,
          });
        } catch {
          // Listing keys are optional for some brokers; save may still succeed.
        }

        await setMarketDataPreference(
          buildMarketDataPreferenceRequest(
            brokerAccountCode,
            chartActiveSymbol,
            selection,
            resolveContext
          )
        );

        const normalized = selection.assetClass.trim().toLowerCase();
        setConfirmedAssetClassBySymbol((prev) => ({ ...prev, [activeSymbolKey]: normalized }));
        dismissAssetClassRequired();
        refetchExecutionCandles();
      } catch (e) {
        const description =
          e instanceof InstrumentsApiError
            ? e.detail || e.message
            : e instanceof Error
              ? e.message
              : "Failed to save market data preference.";
        showApiErrorToast({
          title: "Could not save market data preference",
          description,
          duration: 7000,
        });
      } finally {
        setMdPreferenceSaving(false);
      }
    },
    [
      activeSymbolKey,
      brokerAccountCode,
      chartActiveSymbol,
      dismissAssetClassRequired,
      refetchExecutionCandles,
    ]
  );

  // Cache suggested periods from the initial all-time/preset call (chunk responses omit periods).
  useEffect(() => {
    if (!data || periodWindow || !activeSymbolKey) return;
    setCachedSuggestedPeriods(data.suggested_periods ?? []);
  }, [data, periodWindow, activeSymbolKey]);

  const noTradesForSymbol = data?.message === "no_trades_for_symbol";

  const effectiveChartSymbol = data?.resolved_symbol || chartActiveSymbol;
  const symbolWasResolved =
    Boolean(data?.requested_symbol && data?.resolved_symbol) &&
    normalizeInstrumentSymbol(data!.requested_symbol!) !==
      normalizeInstrumentSymbol(data!.resolved_symbol!);

  const candles = useMemo(() => {
    if (candleTypeMismatch) return [];
    return normalizeExecutionCandles(data?.candles || []);
  }, [data?.candles, candleTypeMismatch]);

  const rawOverlays = data?.trades || [];

  const baselineTradesFromApi = data?.baseline_trades;
  const simulatedTradesFromApi = data?.simulated_trades;
  const managementEventsFromApi = data?.management_events;

  const hasPolicyKeptRemovedIds = Boolean(
    policyOverlay &&
      ((policyOverlay.kept_trade_ids?.length ?? 0) > 0 ||
        (policyOverlay.removed_trade_ids?.length ?? 0) > 0)
  );

  const apiIndicatesTmSimulation = Boolean(
    backtestId?.trim() &&
      !hasPolicyKeptRemovedIds &&
      (data?.trades_cohort_axis === "backtest" ||
        ((baselineTradesFromApi?.length ?? 0) > 0 &&
          (simulatedTradesFromApi?.length ?? 0) > 0))
  );

  const effectiveBaselineVsSimulatedOverlay =
    isBaselineVsSimulatedOverlay || apiIndicatesTmSimulation;

  const simulatedTradesMerged = useMemo(
    () => mergeSimulationTradeRows(backtestSimulationRows, simulatedTradesFromApi),
    [backtestSimulationRows, simulatedTradesFromApi]
  );

  const hasSimulationOverlayData = Boolean(
    backtestId &&
      (simulatedTradesMerged.length > 0 ||
        (effectiveBaselineVsSimulatedOverlay &&
          ((baselineTradesFromApi?.length ?? 0) > 0 ||
            (managementEventsFromApi?.length ?? 0) > 0)))
  );

  /** Kept/removed badges — Entry Analysis veto; also OK alongside Exit Placement changes. */
  const useKeptRemovedPolicyTags = hasPolicyKeptRemovedIds;

  const simulationChangeMap = useMemo(() => {
    // Pure Regime: no sim rows. EP / hybrid: build from nested simulated_trades (and API baseline when present).
    if (
      !effectiveBaselineVsSimulatedOverlay &&
      simulatedTradesMerged.length === 0
    ) {
      return new Map();
    }
    return buildSimulationChangeMap(baselineTradesFromApi, simulatedTradesMerged);
  }, [effectiveBaselineVsSimulatedOverlay, baselineTradesFromApi, simulatedTradesMerged]);

  const chartSecondsPerCandle = useMemo(
    () => executionCandleTypeSeconds(candleType),
    [candleType]
  );

  const showSimulationChangeColumn =
    simulationChangeMap.size > 0 &&
    Array.from(simulationChangeMap.values()).some((info) => info.changed);

  const candleCoverage = useMemo(() => {
    const expected = data?.candles_expected;
    const returned = data?.candles_returned ?? candles.length;
    if (expected == null || expected <= 0) return null;
    return { expected, returned, ratio: returned / expected };
  }, [data?.candles_expected, data?.candles_returned, candles.length]);

  const cohortHighlightSet = useMemo(() => {
    if (!cohortPreviewMode || !cohortHighlightIds?.length) return null;
    return new Set(
      cohortHighlightIds.map((id) => normalizePolicyTradeId(id)).filter(Boolean)
    );
  }, [cohortPreviewMode, cohortHighlightIds]);

  const applyCohortPreviewTags = useCallback(
    (trade: ExecutionTradeOverlay): ExecutionTradeOverlay => {
      if (!cohortHighlightSet) return trade;
      const n = normalizePolicyTradeId(trade.trade_id);
      const inBucket = Boolean(n && cohortHighlightSet.has(n));
      if (!inBucket) {
        return { ...trade, bucket_overlay: "out_of_bucket" };
      }
      return {
        ...trade,
        bucket_overlay: "in_bucket",
        cohort_outcome: cohortOutcomeFromOverlay(trade),
      };
    },
    [cohortHighlightSet]
  );

  const displayTrades = useMemo((): ExecutionTradeOverlay[] => {
    const kept = new Set(
      (policyOverlay?.kept_trade_ids ?? []).map(normalizePolicyTradeId).filter(Boolean)
    );
    const removed = new Set(
      (policyOverlay?.removed_trade_ids ?? []).map(normalizePolicyTradeId).filter(Boolean)
    );
    const policyTag = (tradeId: string | null | undefined): "kept" | "removed" | undefined => {
      if (!useKeptRemovedPolicyTags) return undefined;
      const n = normalizePolicyTradeId(tradeId);
      if (!n) return undefined;
      if (removed.has(n)) return "removed";
      if (kept.has(n)) return "kept";
      return undefined;
    };

    const snapById = new Map<string, Trade>();
    for (const t of activeSymbolTrades) {
      const n = normalizePolicyTradeId(t.id);
      if (n) snapById.set(n, t);
      const alt = normalizePolicyTradeId((t as Trade & { trade_id?: string }).trade_id);
      if (alt && !snapById.has(alt)) snapById.set(alt, t);
    }

    const preferApiOverlayRows = Boolean(
      activeSymbolKey &&
        rawOverlays.length > 0 &&
        (useKeptRemovedPolicyTags || cohortPreviewMode) &&
        !effectiveBaselineVsSimulatedOverlay
    );

    const symbolMatchesActive = (symbol: string | null | undefined): boolean =>
      Boolean(activeSymbolKey && normalizeInstrumentSymbol(symbol ?? "") === activeSymbolKey);

    const mergeOne = (overlay: ExecutionTradeOverlay): ExecutionTradeOverlay => {
      const tid = overlay.trade_id ?? "";
      const snap = tid ? snapById.get(normalizePolicyTradeId(tid)) : undefined;
      let out = snap ? enrichExecutionOverlayFromTrade(overlay, snap) : overlay;
      const p = policyTag(tid) ?? (snap ? policyTag(snap.id) : undefined);
      if (p) out = { ...out, policy_overlay: p };
      const simChange = simulationChangeMap.get(normalizePolicyTradeId(tid));
      if (simChange) {
        out = {
          ...out,
          simulation_overlay: simChange.changed ? "changed" : "unchanged",
        };
        if (simChange.changed) {
          const baselineForSim = snap ? enrichExecutionOverlayFromTrade(overlay, snap) : out;
          const enriched = enrichSimulatedOverlayForChart(
            simChange,
            baselineForSim,
            chartSecondsPerCandle
          );
          const simLevels = simulatedSlTpLevels(enriched, simulatedLegs);
          out = {
            ...out,
            sim_exit_price: enriched.exit_price ?? null,
            sim_exit_time: enriched.exit_time ?? null,
            sim_net_pnl:
              enriched.net_pnl != null
                ? Number(enriched.net_pnl)
                : simChange.simulated.net_pnl != null
                  ? Number(simChange.simulated.net_pnl)
                  : null,
            sim_sl_price: simLevels.slPrice,
            sim_tp_price: simLevels.tpPrice,
          };
        }
      }
      return out;
    };

    const overlayById = new Map<string, ExecutionTradeOverlay>();
    const overlaySource =
      effectiveBaselineVsSimulatedOverlay && baselineTradesFromApi?.length
        ? baselineTradesFromApi
        : rawOverlays;
    for (const o of overlaySource) {
      const n = normalizePolicyTradeId(o.trade_id);
      if (n) overlayById.set(n, o);
    }

    const inReportCohort = (trade: Trade | undefined, overlay?: ExecutionTradeOverlay): boolean => {
      if (!useReportClosedExitCohort || !reviewBounds) return true;
      if (trade) {
        return tradeClosedExitInWindow(trade, reviewBounds.startMs, reviewBounds.endMs);
      }
      if (overlay) {
        return overlayClosedExitInWindow(overlay, reviewBounds.startMs, reviewBounds.endMs);
      }
      return false;
    };

    const rows: ExecutionTradeOverlay[] = preferApiOverlayRows
      ? rawOverlays
          .filter((o) => symbolMatchesActive(o.symbol))
          .filter((o) => {
            if (!useReportClosedExitCohort || !reviewBounds) return true;
            const snap = snapById.get(normalizePolicyTradeId(o.trade_id));
            return inReportCohort(snap, o);
          })
          .map((o) => mergeOne(o))
      : chartSymbolTrades.map((t) => {
          const n = normalizePolicyTradeId(t.id);
          const fromApi = n ? overlayById.get(n) : undefined;
          const base = fromApi ? mergeOne(fromApi) : tradeToExecutionOverlay(t);
          const p = policyTag(t.id);
          return p ? { ...base, policy_overlay: p } : base;
        });

    if (useKeptRemovedPolicyTags) {
      const seen = new Set(rows.map((r) => normalizePolicyTradeId(r.trade_id)).filter(Boolean));
      Array.from(removed).forEach((id) => {
        if (seen.has(id)) return;
        const trade = snapById.get(id);
        const fromApi = overlayById.get(id);
        if (!trade && !fromApi) return;
        if (!inReportCohort(trade, fromApi)) return;
        const base = fromApi ? mergeOne(fromApi) : tradeToExecutionOverlay(trade!);
        rows.push({ ...base, policy_overlay: "removed" });
        seen.add(id);
      });
    }

    const enrichOutcome = (trade: ExecutionTradeOverlay): ExecutionTradeOverlay => ({
      ...trade,
      cohort_outcome: trade.cohort_outcome ?? cohortOutcomeFromOverlay(trade),
    });

    if (rows.length === 0 && overlaySource.length > 0) {
      return overlaySource
        .filter((o) => {
          if (!useReportClosedExitCohort || !reviewBounds) return true;
          const snap = snapById.get(normalizePolicyTradeId(o.trade_id));
          return inReportCohort(snap, o);
        })
        .map(mergeOne)
        .map(applyCohortPreviewTags)
        .map(enrichOutcome);
    }

    return rows.map(applyCohortPreviewTags).map(enrichOutcome);
  }, [
    activeSymbolTrades,
    activeSymbolKey,
    cohortPreviewMode,
    rawOverlays,
    baselineTradesFromApi,
    chartSymbolTrades,
    policyOverlay?.kept_trade_ids,
    policyOverlay?.removed_trade_ids,
    effectiveBaselineVsSimulatedOverlay,
    useKeptRemovedPolicyTags,
    simulationChangeMap,
    chartSecondsPerCandle,
    simulatedLegs,
    applyCohortPreviewTags,
    useReportClosedExitCohort,
    reviewBounds,
  ]);

  const tradesForList = useMemo((): ExecutionTradeOverlay[] => {
    let rows = displayTrades;
    if (cohortPreviewMode) {
      rows = filterTradesForCohortPreview(rows, cohortListFilter, cohortHighlightSet);
    } else if (useKeptRemovedPolicyTags && policyListFilter !== "all") {
      rows = filterTradesForPolicyOverlay(rows, policyListFilter);
    } else if (showSimulationChangeColumn && simulationListFilter !== "all") {
      rows = filterTradesForSimulationOverlay(rows, simulationListFilter);
    }
    return rows;
  }, [
    cohortHighlightSet,
    cohortListFilter,
    cohortPreviewMode,
    displayTrades,
    policyListFilter,
    showSimulationChangeColumn,
    simulationListFilter,
    useKeptRemovedPolicyTags,
  ]);

  const tradesForChart = useMemo((): ExecutionTradeOverlay[] => {
    let rows = displayTrades;
    if (cohortPreviewMode) {
      rows = filterTradesForCohortPreview(rows, cohortListFilter, cohortHighlightSet);
    } else if (useKeptRemovedPolicyTags && policyListFilter !== "all") {
      rows = filterTradesForPolicyOverlay(rows, policyListFilter);
    } else if (showSimulationChangeColumn && simulationListFilter !== "all") {
      rows = filterTradesForSimulationOverlay(rows, simulationListFilter);
    }
    return rows;
  }, [
    cohortHighlightSet,
    cohortListFilter,
    cohortPreviewMode,
    displayTrades,
    policyListFilter,
    showSimulationChangeColumn,
    simulationListFilter,
    useKeptRemovedPolicyTags,
  ]);

  useEffect(() => {
    if (!onCohortPreviewStats) return;
    if (!cohortPreviewMode || !cohortHighlightIds?.length) {
      onCohortPreviewStats(null);
      return;
    }
    // Header metrics follow the same tab filter as the list/chart (not always full cohort).
    const scoped = filterTradesForCohortPreview(
      displayTrades,
      cohortListFilter,
      cohortHighlightSet
    );
    const scopedIds = scoped
      .map((t) => String(t.trade_id ?? "").trim())
      .filter(Boolean);
    onCohortPreviewStats(computeCohortPreviewStatsFromOverlays(scopedIds, scoped));
  }, [
    cohortHighlightIds,
    cohortHighlightSet,
    cohortListFilter,
    cohortPreviewMode,
    displayTrades,
    onCohortPreviewStats,
  ]);

  const cohortHighlightMatchCount = useMemo(() => {
    if (!cohortPreviewMode || !cohortHighlightIds?.length) return 0;
    return displayTrades.filter((t) => t.bucket_overlay === "in_bucket").length;
  }, [cohortHighlightIds?.length, cohortPreviewMode, displayTrades]);

  const policyRemovedMatchCount = useMemo(() => {
    const tagged = displayTrades.filter((t) => t.policy_overlay === "removed").length;
    if (tagged > 0) return tagged;
    const removedIds = new Set(
      (policyOverlay?.removed_trade_ids ?? []).map(normalizePolicyTradeId).filter(Boolean)
    );
    if (removedIds.size === 0) return 0;
    let fromOverlays = 0;
    for (const o of rawOverlays) {
      const n = normalizePolicyTradeId(o.trade_id);
      if (n && removedIds.has(n)) fromOverlays += 1;
    }
    return fromOverlays;
  }, [displayTrades, policyOverlay?.removed_trade_ids, rawOverlays]);

  const policyOverlayStats = useMemo(() => {
    if (!useKeptRemovedPolicyTags) return null;
    return computePolicyOverlayStats(displayTrades);
  }, [displayTrades, useKeptRemovedPolicyTags]);

  /** Metrics for the active policy filter — All defaults to Affected (removed). */
  const policyOverlayDirectionStats = useMemo(() => {
    if (!useKeptRemovedPolicyTags) return null;
    const scoped =
      policyListFilter === "all"
        ? displayTrades.filter((t) => t.policy_overlay === "removed")
        : filterTradesForPolicyOverlay(displayTrades, policyListFilter);
    return computeOverlayDirectionStats(scoped);
  }, [displayTrades, policyListFilter, useKeptRemovedPolicyTags]);

  const simulationOverlayDirectionStats = useMemo(() => {
    if (useKeptRemovedPolicyTags || !showSimulationChangeColumn) return null;
    const scoped =
      simulationListFilter === "all"
        ? displayTrades.filter((t) => t.simulation_overlay === "changed")
        : filterTradesForSimulationOverlay(displayTrades, simulationListFilter);
    return computeOverlayDirectionStats(scoped);
  }, [
    displayTrades,
    showSimulationChangeColumn,
    simulationListFilter,
    useKeptRemovedPolicyTags,
  ]);

  const simulationOverlayTaggedCount = useMemo(() => {
    if (useKeptRemovedPolicyTags || !showSimulationChangeColumn) return 0;
    let n = 0;
    for (const t of displayTrades) {
      if (t.simulation_overlay === "changed" || t.simulation_overlay === "unchanged") n += 1;
    }
    return n;
  }, [displayTrades, showSimulationChangeColumn, useKeptRemovedPolicyTags]);

  useEffect(() => {
    setPolicyListFilter("all");
  }, [policyOverlay?.kept_trade_ids, policyOverlay?.removed_trade_ids]);

  useEffect(() => {
    if (policyListFilter === "changed" && !showSimulationChangeColumn) {
      setPolicyListFilter("all");
    }
  }, [policyListFilter, showSimulationChangeColumn]);

  useEffect(() => {
    setSimulationListFilter("all");
  }, [backtestId, effectiveBaselineVsSimulatedOverlay]);

  const resolvedWindow = data?.resolved_window ?? null;

  useEffect(() => {
    if (respectUserLocalPeriodForCandles) {
      setSpanExpandedLocalWindow(null);
      return;
    }
    if (!baseLocalCandleWindow || !resolvedWindow?.span_start) return;
    const covered = expandLocalCandleWindowToCoverTradeTimes(
      baseLocalCandleWindow,
      tradeTimesForCandleWindow
    );
    const withSpan = expandLocalCandleWindowToCoverResolvedSpan(covered, resolvedWindow);
    if (localCandleWindowsEqual(covered, withSpan)) return;
    setSpanExpandedLocalWindow((prev) =>
      localCandleWindowsEqual(prev, withSpan) ? prev : withSpan
    );
  }, [
    baseLocalCandleWindow,
    resolvedWindow,
    tradeTimesForCandleWindow,
    respectUserLocalPeriodForCandles,
  ]);

  const symbolChartSpanLabel = useMemo(() => {
    if (!resolvedWindow || !chartActiveSymbol) return null;
    return `${formatLocalDateTime(resolvedWindow.span_start)} → ${formatLocalDateTime(resolvedWindow.span_end)}`;
  }, [resolvedWindow, chartActiveSymbol]);

  const chartWindowLabel = useMemo(() => {
    if (resolvedWindow) {
      return `${formatLocalDateTime(resolvedWindow.window_start)} → ${formatLocalDateTime(resolvedWindow.window_end)}`;
    }
    if (data?.start_time && data?.end_time) {
      return `${formatLocalDateTime(data.start_time)} → ${formatLocalDateTime(data.end_time)}`;
    }
    return null;
  }, [resolvedWindow, data?.start_time, data?.end_time]);

  /** Live response wins; use cache only while viewing an explicit period chunk. */
  const suggestedPeriods = periodWindow
    ? cachedSuggestedPeriods
    : (data?.suggested_periods ?? []);

  const activePeriodKey = periodWindow
    ? `${periodWindow.start}\0${periodWindow.end}`
    : suggestedPeriods.find((p) => p.is_current)
      ? periodKey(suggestedPeriods.find((p) => p.is_current)!)
      : null;

  const showPeriodChips = suggestedPeriods.length > 1;

  const chartWindowKey = useMemo(() => {
    let windowKey = "default";
    if (periodWindow) {
      windowKey = `${periodWindow.start}:${periodWindow.end}`;
    } else if (executionCandlesRequestWindow?.kind === "timeframe") {
      windowKey = executionCandlesRequestWindow.timeframe;
    } else if (executionCandlesRequestWindow?.kind === "date") {
      windowKey = executionCandlesRequestWindow.date;
    } else if (executionCandlesRequestWindow?.kind === "trade_list") {
      windowKey =
        executionCandlesRequestWindow.window.kind === "timeframe"
          ? executionCandlesRequestWindow.window.timeframe
          : `${executionCandlesRequestWindow.window.period.local_start_date}:${executionCandlesRequestWindow.window.period.local_end_date}`;
    } else if (executionCandlesRequestWindow?.kind === "local") {
      windowKey = `${executionCandlesRequestWindow.period.local_start_date}:${executionCandlesRequestWindow.period.local_end_date}`;
    } else if (executionCandlesRequestWindow?.kind === "range") {
      windowKey = `${executionCandlesRequestWindow.start_date}:${executionCandlesRequestWindow.end_date}`;
    }
    return `${chartActiveSymbol}|${windowKey}`;
  }, [chartActiveSymbol, periodWindow, executionCandlesRequestWindow]);

  useEffect(() => {
    setBarBudgetMinCandleType(null);
    setCoarseningNotice(null);
    coarseningNoticeBoundKeyRef.current = null;
  }, [chartWindowKey]);

  useEffect(() => {
    if (loading || !data) return;
    const requested = pickRequestedExecutionCandleType(data, candleType);
    setBarBudgetMinCandleType(pickExecutionCandlesBarBudgetFloor(data, requested));

    const resolved = pickResolvedExecutionCandleType(data);
    if (!resolved || !isExecutionCandlesTypeCoarsened(data, requested)) return;
    if (coarseningNoticeBoundKeyRef.current === chartWindowKey) return;

    coarseningNoticeBoundKeyRef.current = chartWindowKey;
    setCoarseningNotice({
      requestedLabel: formatExecutionCandleTypeLabel(requested),
      resolvedLabel: formatExecutionCandleTypeLabel(resolved),
      reason: getUserFacingCandleResolutionReason(data.candle_resolution_reason),
    });
  }, [data, loading, chartWindowKey, candleType]);

  const handleCandleTypeSelect = useCallback(
    (next: string) => {
      if (reportCandleTypeLocked) return;
      setCandleType(next);
      coarseningNoticeBoundKeyRef.current = null;
      setCoarseningNotice(null);
    },
    [reportCandleTypeLocked]
  );

  const handleInstrumentChange = useCallback(
    (raw: string) => {
      const next = raw.trim();
      if (!next) return;
      if (forceChartSymbol?.trim()) return;
      const match = instrumentOptions.find(
        (o) =>
          normalizeInstrumentSymbol(o.symbol) === normalizeInstrumentSymbol(next)
      );
      setChartActiveSymbol(match?.symbol ?? next);
    },
    [forceChartSymbol, instrumentOptions]
  );

  // Fill the viewport with the chart; leave room for legend + ~1 trade row.
  useEffect(() => {
    const measure = () => {
      const reserve = isReportsEmbed ? 340 : 420;
      setChartHeightPx(Math.max(480, Math.round(window.innerHeight - reserve)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isReportsEmbed]);

  // Auto-enable Lane B on the chart from cohort bucket / report picks / policy overlay.
  useEffect(() => {
    if (!autoLaneBIds.length) return;
    setEnabledLaneBOverlays((prev) => {
      const next = new Set(prev);
      for (const id of autoLaneBIds) next.add(id);
      next.add("regime");
      if (next.size === prev.length && prev.every((id) => next.has(id))) {
        return prev;
      }
      return Array.from(next);
    });
  }, [autoLaneBIds]);

  const reportTfDiverged = useMemo(() => {
    if (!reportCandleTypeLocked || !lockedDemoCandleType) return false;
    return isReportCandleTfDiverged(candleType, lockedDemoCandleType);
  }, [reportCandleTypeLocked, lockedDemoCandleType, candleType]);

  const laneBOverlaysActiveForChart = useMemo(() => {
    if (reportTfDiverged) {
      // Hide locked pick overlays until user returns to report TF.
      return [] as TesseraLaneBOverlayId[];
    }
    const ids = new Set(enabledLaneBOverlays);
    ids.add("regime");
    return Array.from(ids);
  }, [reportTfDiverged, enabledLaneBOverlays]);

  const regimeOverlayActive = Boolean(!reportTfDiverged && data?.regime_overlay);

  const handleChangeMarketDataSource = useCallback(() => {
    if (ohlcSetupGate.connectGate) {
      ohlcSetupGate.openMarketDataSetup();
      return;
    }
    const code = effectiveBrokerAccountCode;
    if (!code) return;
    void openSetup(code, {
      mandatory: false,
      forceOpen: true,
      focusSymbol: chartActiveSymbol?.trim() || undefined,
    });
  }, [
    chartActiveSymbol,
    effectiveBrokerAccountCode,
    ohlcSetupGate,
    openSetup,
  ]);

  const resolvedChartCandleType = resolvedDataCandleType;
  const coarsenedForSelection = Boolean(
    data && resolvedChartCandleType && isExecutionCandlesTypeCoarsened(data, candleType)
  );
  const displayChartCandleType =
    resolvedChartCandleType &&
    (executionCandleTypesMatch(candleType, resolvedChartCandleType) || coarsenedForSelection)
      ? resolvedChartCandleType
      : normalizeExecutionCandleType(candleType) ?? candleType;

  const executionCandlesMaxBars = data?.max_bars ?? DEFAULT_EXECUTION_CANDLES_MAX_BARS;
  const showBackfillIncompleteAlert =
    !loading && Boolean(data) && isExecutionCandlesBackfillIncomplete(data) && !backfillLoading;

  const isBackfillProgressError =
    typeof error === "string" && /backfill in progress/i.test(error);

  const candleTypeMenuOptions = useMemo(() => {
    if (reportCandleTypeLocked && lockedDemoCandleType) {
      const locked =
        candleTypeOptions.find((o) => o.value === lockedDemoCandleType) ?? {
          value: lockedDemoCandleType,
          label: formatExecutionCandleTypeLabel(lockedDemoCandleType),
        };
      return [{ value: locked.value, label: locked.label, disabled: false }];
    }
    return candleTypeOptions.map((o) => ({
      value: o.value,
      label: o.label,
      disabled: isExecutionCandleTypeDisabledForBarBudget(o.value, barBudgetMinCandleType),
    }));
  }, [barBudgetMinCandleType, reportCandleTypeLocked, lockedDemoCandleType]);

  const allowedCandleTypesForChart = useMemo(
    () => candleTypeMenuOptions.filter((o) => !o.disabled).map((o) => o.value),
    [candleTypeMenuOptions]
  );

  const databentoRecentTailMessage = !loading ? getDatabentoRecentTailMessage(data) : null;
  const historyLimitMessage = !loading ? getExecutionCandlesHistoryLimitMessage(data) : null;
  const historyLimitSuggestedCandleTypes = !loading
    ? getExecutionCandlesSuggestedCandleTypes(data)
    : [];
  const partialLoadMessage = !loading ? getExecutionCandlesPartialMessage(data) : null;

  const handleScrollToTradeHandled = useCallback(() => {
    setScrollToTradeId(null);
  }, []);

  const handleSelectTradeFromList = (tradeId: string | null) => {
    setSelectedTradeId(tradeId);
    // Pan/highlight on chart only — do not refetch execution-candles (data already loaded).
    setFocusTradeId(tradeId);
    setScrollToTradeId(null);
  };

  const handleSelectTradeFromChart = (tradeId: string | null) => {
    setSelectedTradeId(tradeId);
    // Keep the full trades table; only scroll/highlight the selected row.
    setScrollToTradeId(tradeId);
    // Do not focus-pan — the trade was clicked on-chart so it is already visible.
    if (tradeId && collapsibleTradesList) {
      setTradesListOpen(true);
    }
  };

  // Remount only when symbol / date window / candle type changes — not when All/Kept/Removed
  // (or sim/cohort) list filters change, or the chart viewport resets on every tab switch.
  const chartRenderKey = useMemo(
    () => `${chartWindowKey}|${displayChartCandleType}`,
    [chartWindowKey, displayChartCandleType]
  );

  const chartLoadWindowMs = useMemo(() => {
    if (periodWindow) {
      const start = new Date(periodWindow.start).getTime();
      const end = new Date(periodWindow.end).getTime();
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) return end - start;
    }
    if (resolvedWindow?.window_start && resolvedWindow?.window_end) {
      const start = new Date(resolvedWindow.window_start).getTime();
      const end = new Date(resolvedWindow.window_end).getTime();
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) return end - start;
    }
    if (data?.start_time && data?.end_time) {
      const start = new Date(data.start_time).getTime();
      const end = new Date(data.end_time).getTime();
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) return end - start;
    }
    if (reviewBounds) return reviewBounds.endMs - reviewBounds.startMs;
    return 0;
  }, [periodWindow, resolvedWindow, data?.start_time, data?.end_time, reviewBounds]);

  const isFineCandleType = displayChartCandleType === "m" || displayChartCandleType === "5m";
  const fineCandleLabel = displayChartCandleType === "m" ? "1-minute" : "5-minute";
  const showFineCandleLoadNotice =
    isFineCandleType &&
    (chartLoadWindowMs > TWO_WEEKS_MS || (isAllTime && chartLoadWindowMs === 0));
  const emptyPanelBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const tradesListCountSuffix =
    cohortPreviewMode && cohortListFilter !== "all"
      ? ` / ${displayTrades.length}`
      : !cohortPreviewMode && useKeptRemovedPolicyTags && policyListFilter !== "all"
        ? ` / ${displayTrades.length}`
        : !cohortPreviewMode &&
            showSimulationChangeColumn &&
            simulationListFilter !== "all"
          ? ` / ${displayTrades.length}`
          : "";
  const tradesListTitle = `Trades (${tradesForList.length}${tradesListCountSuffix})`;

  return (
    <>
    {assetClassRequired ? (
      <MarketDataAssetClassRequiredModal
        isOpen={mdAssetClassModalOpen}
        onClose={dismissAssetClassRequired}
        payload={assetClassRequired}
        saving={mdPreferenceSaving}
        onConfirm={handleMarketDataAssetClassConfirm}
      />
    ) : null}
    <MarketDataQuoteVariantModal
      isOpen={Boolean(instrumentResolve.pendingQuoteVariantChoice)}
      onClose={() => instrumentResolve.reset()}
      tradeSymbol={instrumentResolve.pendingQuoteVariantChoice?.tradeSymbol ?? chartActiveSymbol}
      candidates={instrumentResolve.pendingQuoteVariantChoice?.candidates ?? []}
      suggested={instrumentResolve.pendingQuoteVariantChoice?.suggested}
      saving={instrumentResolve.savingQuoteVariant}
      onConfirm={(qualifiedSymbol) => {
        void instrumentResolve.confirmQuoteVariantSelection(qualifiedSymbol).then(() => {
          refetchExecutionCandles();
        });
      }}
    />
    <Box
      {...(isReportsEmbed
        ? { "data-reports-trade-review": "", w: "100%" }
        : {
            bg: cardBg,
            borderRadius: "lg",
            border: "1px solid",
            borderColor,
            p: 4,
            w: "100%",
          })}
    >
      {!suppressHeader &&
      ((isAllTime && symbolChartSpanLabel) ||
        chartWindowLabel ||
        (symbolWasResolved && data?.requested_symbol) ||
        (symbolDiscovery && symbolsLoading) ||
        symbolsError) ? (
        <Box mb={3} minW={0} w="100%">
          {isAllTime && symbolChartSpanLabel ? (
            <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
              Chart for {chartActiveSymbol}: {symbolChartSpanLabel}
              {chartWindowLabel ? ` · loading ${chartWindowLabel}` : ""}
            </Text>
          ) : chartWindowLabel ? (
            <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
              Chart OHLC window: {chartWindowLabel}
            </Text>
          ) : null}
          {symbolWasResolved && data?.requested_symbol ? (
            <Text
              fontSize="xs"
              color={secondaryTextColor}
              mt={chartWindowLabel || (isAllTime && symbolChartSpanLabel) ? 2 : 0}
              fontFamily="'IBM Plex Sans', sans-serif"
            >
              Chart symbol auto-resolved from {data.requested_symbol}
            </Text>
          ) : null}
          {symbolDiscovery && symbolsLoading ? (
            <Flex align="center" gap={2} mt={2}>
              <Spinner size="xs" color="cyan.400" />
              <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif">
                Loading symbols…
              </Text>
            </Flex>
          ) : null}
          {symbolsError ? (
            <Text fontSize="xs" color="orange.300" mt={1} fontFamily="'IBM Plex Sans', sans-serif">
              {symbolsError}
            </Text>
          ) : null}
        </Box>
      ) : null}

      <div
        data-trade-review-controls=""
        className="mb-3 flex w-full min-w-0 flex-wrap items-end gap-x-4 gap-y-2"
      >
        <Checkbox
          size="sm"
          w="fit-content"
          maxW="100%"
          isChecked={includeDust}
          onChange={(e) => setIncludeDust(e.target.checked)}
          colorScheme="cyan"
          data-trade-review-field="dust"
        >
          <Text
            fontSize="xs"
            color={secondaryTextColor}
            fontFamily="var(--font-primary)"
            whiteSpace="nowrap"
          >
            Include dust & scratch positions
          </Text>
        </Checkbox>

        {windowMode === "date" ? (
          <Field className="w-fit min-w-0 gap-1" data-trade-review-field="date">
            <FieldLabel htmlFor="trade-review-date" className={TRADE_REVIEW_FIELD_LABEL_CLASS}>
              Date (single day)
            </FieldLabel>
            <Input
              id="trade-review-date"
              size="sm"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              bg={inputBg}
              borderColor={borderColor}
              fontFamily="var(--font-primary)"
              max={toYmd(new Date())}
              borderRadius="6px"
              h="32px"
            />
          </Field>
        ) : null}

        {beforeCandleTypeControls ? (
          <Field className="w-fit min-w-0 gap-1" data-trade-review-field="overlay">
            <FieldLabel htmlFor="trade-review-simulation-overlay" className={TRADE_REVIEW_FIELD_LABEL_CLASS}>
              Simulation overlay
            </FieldLabel>
            {beforeCandleTypeControls}
          </Field>
        ) : null}
      </div>

      {regimeOverlayActive ? (
        <ResearchDisclaimerNotice kind="regime" className="mb-3" />
      ) : null}

      {reportTfDiverged ? (
        <Alert variant="warning" className={`${TRADE_REVIEW_ALERT_CLASS} mb-3`}>
          <TriangleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            Chart candle type differs from the report timeframe (
            {formatExecutionCandleTypeLabel(lockedDemoCandleType || "")}
            ). Locked report overlays are hidden until you return to the report candle type.
          </AlertDescription>
        </Alert>
      ) : null}

      {useKeptRemovedPolicyTags && policyOverlayStats && policyOverlayStats.totalTagged > 0 ? (
        <PolicyOverlaySummary
          listFilter={policyListFilter}
          onListFilterChange={setPolicyListFilter}
          includeChanged={showSimulationChangeColumn}
          directionStats={policyOverlayDirectionStats}
          scopeTotal={policyOverlayStats.totalTagged}
        />
      ) : null}

      {!useKeptRemovedPolicyTags && showSimulationChangeColumn ? (
        <SimulationOverlaySummary
          listFilter={simulationListFilter}
          onListFilterChange={setSimulationListFilter}
          directionStats={simulationOverlayDirectionStats}
          scopeTotal={simulationOverlayTaggedCount}
        />
      ) : null}

      {showPeriodChips ? (
        <Box mb={3}>
          <Text fontSize="xs" color={secondaryTextColor} fontFamily="'IBM Plex Sans', sans-serif" mb={2}>
            Chart periods. Trade list is for the full filter
          </Text>
          <HStack spacing={2} flexWrap="wrap">
            {suggestedPeriods.map((p) => {
              const key = periodKey(p);
              const isActive = activePeriodKey === key;
              return (
                <Button
                  key={key}
                  size="xs"
                  variant={isActive ? "solid" : "outline"}
                  colorScheme="cyan"
                  onClick={() => {
                    if (p.is_current) {
                      setPeriodWindow(null);
                    } else {
                      setPeriodWindow({ start: p.window_start, end: p.window_end });
                    }
                  }}
                >
                  {formatPeriodChipLabel(p.window_start, p.window_end)}
                  {p.trade_count > 0 ? ` (${p.trade_count})` : ""}
                </Button>
              );
            })}
          </HStack>
        </Box>
      ) : null}

      {brokerConsentRepair ? (
        <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
          <TriangleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            {brokerConsentRepair.message ??
              "Reconnect your broker and grant market data consent to load OHLC from your account."}{" "}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 align-baseline"
              onClick={() => {
                history.push(
                  buildConnectBrokerManagementOpenUrl({ brokerName: brokerConsentRepair.broker })
                );
                dismissBrokerConsentRepair();
              }}
            >
              Open broker settings
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {coarseningNotice ? (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            Chart uses {coarseningNotice.resolvedLabel} candles for this period (
            {coarseningNotice.requestedLabel} is too fine for the {executionCandlesMaxBars.toLocaleString()}
            -bar limit). Shorten the date range
            {showPeriodChips ? " or pick a smaller chart period above" : ""} to plot finer candles.
            {coarseningNotice.reason ? (
              <span className="mt-1 block text-xs opacity-90">{coarseningNotice.reason}</span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {databentoRecentTailMessage ? (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            {databentoRecentTailMessage}
          </AlertDescription>
        </Alert>
      ) : null}

      {historyLimitMessage && !chartUnavailablePayload ? (
        <ExecutionCandlesHistoryLimitBanner
          message={historyLimitMessage}
          suggestedCandleTypes={historyLimitSuggestedCandleTypes}
          currentCandleType={candleType}
          barBudgetMinCandleType={barBudgetMinCandleType}
          onSelectCandleType={handleCandleTypeSelect}
          disabled={reportCandleTypeLocked}
        />
      ) : null}

      {showBackfillIncompleteAlert ? (
        <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
          <TriangleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            {partialLoadMessage ??
              "Price data for this window is incomplete. The chart may look wrong until historical OHLC backfill catches up."}
          </AlertDescription>
        </Alert>
      ) : null}

      {backfillLoading && !loading ? (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            Loading historical price data
            {backfillProgress?.total_chunks
              ? ` (${backfillProgress.done_chunks ?? 0} / ${backfillProgress.total_chunks} chunks)`
              : "…"}
          </AlertDescription>
        </Alert>
      ) : null}

      {partialLoadMessage &&
        !showBackfillIncompleteAlert &&
        !databentoRecentTailMessage &&
        !historyLimitMessage &&
        !loading ? (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            {partialLoadMessage}
          </AlertDescription>
        </Alert>
      ) : null}

      {!brokerAccountCode && !demoJobIdProp?.trim() && (
        <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
          <TriangleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            Connect your broker account to use Trade Review.
          </AlertDescription>
        </Alert>
      )}

      {noTradesForSymbol && (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            No trades for {chartActiveSymbol || "this symbol"} in your account. Try another instrument or widen the
            filter.
          </AlertDescription>
        </Alert>
      )}

      {instrumentOptions.length === 0 && reviewBounds && !symbolsLoading && (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            {useReportClosedExitCohort
              ? "No closed trades with an exit in this period"
              : "No trades overlap this period"}
            {includeDust ? "" : " (dust & scratch positions are hidden)"}.
          </AlertDescription>
        </Alert>
      )}

      {error &&
        !chartShellPending &&
        !backfillLoading &&
        !isBackfillProgressError &&
        candles.length === 0 &&
        !chartUnavailablePayload && (
        <Alert variant="error" className={TRADE_REVIEW_ALERT_CLASS}>
          <CircleAlert aria-hidden />
          <AlertTitle className={TRADE_REVIEW_ALERT_TITLE_CLASS}>
            Error loading execution candles.
          </AlertTitle>
          {typeof error === "string" ? (
            <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>{error}</AlertDescription>
          ) : null}
        </Alert>
      )}

      {candleCoverage &&
        candleCoverage.ratio < 0.85 &&
        isExecutionCandlesBackfillIncomplete(data) &&
        !coarseningNotice &&
        !isExecutionCandlesHistoryLimited(data) && (
        <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
          <TriangleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            Price data may be incomplete ({Math.round(candleCoverage.ratio * 100)}% of expected bars). The chart can look
            distorted until broker backfill improves.
          </AlertDescription>
        </Alert>
      )}

      {rawOverlays.length === 0 && chartSymbolTrades.length > 0 && candles.length > 0 && !loading && (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            {chartSymbolTrades.length} trade{chartSymbolTrades.length !== 1 ? "s" : ""} in Trade Log for this period.
            Showing from your trade list (chart markers use trade-log data).
          </AlertDescription>
        </Alert>
      )}

      {useKeptRemovedPolicyTags &&
      (policyOverlay?.removed_trade_ids?.length ?? 0) > 0 &&
      policyRemovedMatchCount === 0 &&
      !loading ? (
        <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
          <TriangleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            Simulation lists {policyOverlay!.removed_trade_ids!.length} removed trade
            {policyOverlay!.removed_trade_ids!.length !== 1 ? "s" : ""}, but none match{" "}
            {chartActiveSymbol ? (
              <>
                <strong>{chartActiveSymbol}</strong> or
              </>
            ) : null}{" "}
            the loaded trade list for this chart. Open the instrument the simulation used (see sim scope
            symbol), or widen the trade filter if trades are missing from the log.
          </AlertDescription>
        </Alert>
      ) : null}

      {cohortPreviewMode &&
      (cohortHighlightIds?.length ?? 0) > 0 &&
      cohortHighlightMatchCount === 0 &&
      !loading ? (
        <Alert variant="warning" className={TRADE_REVIEW_ALERT_CLASS}>
          <TriangleAlert aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            Resolved {cohortHighlightIds!.length} trade
            {cohortHighlightIds!.length !== 1 ? "s" : ""} for this bucket, but none match the trades loaded on this
            chart
            {chartActiveSymbol ? (
              <>
                {" "}
                for <strong>{chartActiveSymbol}</strong>
              </>
            ) : null}
            . Try another instrument from the report, or refresh if the report snapshot is out of date.
          </AlertDescription>
        </Alert>
      ) : null}

      {!chartShellPending && !error && !noTradesForSymbol && !chartUnavailablePayload && candles.length === 0 && brokerAccountCode && chartActiveSymbol && (
        <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
            No price candles returned for this window. Try another period or a coarser candle type.
          </AlertDescription>
        </Alert>
      )}

      <Box mb={4}>
        <Box position="relative" minH={`${chartHeightPx}px`} w="100%">
          {!chartOhlcBlocked ? (
            <ExecutionCandlesChart
              key={chartRenderKey}
              visualStyle={visualStyle}
              candles={candles}
              trades={tradesForChart}
              baselineTrades={hasSimulationOverlayData ? baselineTradesFromApi : undefined}
              simulatedTrades={hasSimulationOverlayData ? simulatedTradesMerged : undefined}
              simulatedLegs={hasSimulationOverlayData ? simulatedLegs : undefined}
              managementEvents={hasSimulationOverlayData ? managementEventsFromApi : undefined}
              simulationLineOverlay={showSimulationChangeColumn && hasSimulationOverlayData}
              selectedTradeId={selectedTradeId}
              showPnlBackgroundOverlay
              showRegimeOverlay={!reportTfDiverged}
              regimeOverlay={!reportTfDiverged ? data?.regime_overlay ?? null : null}
              overlays={reportTfDiverged ? null : data?.overlays ?? null}
              enabledLaneBOverlays={laneBOverlaysActiveForChart}
              candleType={candleType}
              allowedCandleTypes={allowedCandleTypesForChart}
              lockTimeframe={reportCandleTypeLocked}
              lockInstrument={Boolean(forceChartSymbol?.trim())}
              onCandleTypeChange={handleCandleTypeSelect}
              onInstrumentChange={handleInstrumentChange}
              focusTradeId={focusTradeId}
              onFocusTradeHandled={() => setFocusTradeId(null)}
              onSelectTrade={handleSelectTradeFromChart}
              instrumentLabel={effectiveChartSymbol || undefined}
              height={chartHeightPx}
            />
          ) : null}
          {chartShellPending ? <EmbeddedLoadingChart /> : null}
          {!chartShellPending && chartUnavailablePayload ? (
            <MarketDataUnavailableChartOverlay
              payload={chartUnavailablePayload}
              onChangeDataSource={handleChangeMarketDataSource}
              currentCandleType={candleType}
              barBudgetMinCandleType={barBudgetMinCandleType}
              onPickCandleType={handleCandleTypeSelect}
            />
          ) : null}
        </Box>
        {!chartShellPending && candles.length > 0 ? (
          <MarketDataAttributionNotice
            className="mt-3"
            attribution={data?.market_data_attribution}
            sourceLabel={data?.market_data_source_label}
          />
        ) : null}
        <Box
          mt={2}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          px={3}
          py={2.5}
          minH="42px"
          w="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <div className="flex w-full flex-col items-center gap-3">
            {chartShellPending ? (
              <p className="m-0 text-center font-[family-name:var(--font-primary)] text-sm font-medium leading-snug text-[color:var(--brand-tropical-surf)]">
                {showFineCandleLoadNotice
                  ? `${fineCandleLabel} candles over a longer period, still loading…`
                  : backfillLoading
                    ? `Backfilling candles for ${effectiveChartSymbol || "this instrument"}…`
                    : `Building chart for ${effectiveChartSymbol || "this instrument"}…`}
              </p>
            ) : chartUnavailablePayload ? (
              <p className="m-0 text-center font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-muted-foreground">
                Chart unavailable for {effectiveChartSymbol || "this instrument"}.
              </p>
            ) : (
              <div
                data-trade-review-chart-legend=""
                aria-label="Trade review chart legend"
                className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
              >
                {useKeptRemovedPolicyTags ? (
                <>
                  <span className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
                    <span className="font-[family-name:var(--font-primary)] text-xs font-semibold leading-none text-gray-600 dark:text-sidebar-foreground">
                      Kept
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="size-2 shrink-0 rounded-full bg-[#06E363]" aria-hidden />
                      <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-gray-500 dark:text-sidebar-muted-foreground">
                        Long entry
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="size-2 shrink-0 rounded-full bg-[#E00101]" aria-hidden />
                      <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-gray-500 dark:text-sidebar-muted-foreground">
                        Short entry
                      </span>
                    </span>
                  </span>
                  <span
                    className="hidden h-3 w-px shrink-0 bg-gray-200 sm:block dark:bg-sidebar-border"
                    aria-hidden
                  />
                  <span className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
                    <span className="font-[family-name:var(--font-primary)] text-xs font-semibold leading-none text-gray-600 dark:text-sidebar-foreground">
                      Removed
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <TradeReviewChartLegendArrow direction="long" className="shrink-0" />
                      <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#06E363]">
                        Long Win
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <TradeReviewChartLegendArrow direction="long" className="shrink-0" />
                      <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#E00101]">
                        Long Loss
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <TradeReviewChartLegendArrow direction="short" className="shrink-0" />
                      <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#06E363]">
                        Short Win
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <TradeReviewChartLegendArrow direction="short" className="shrink-0" />
                      <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#E00101]">
                        Short Loss
                      </span>
                    </span>
                  </span>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="size-2 shrink-0 rounded-full bg-[#06E363]" aria-hidden />
                    <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-gray-500 dark:text-sidebar-muted-foreground">
                      Long entry
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="size-2 shrink-0 rounded-full bg-[#E00101]" aria-hidden />
                    <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-gray-500 dark:text-sidebar-muted-foreground">
                      Short entry
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <TradeReviewChartLegendArrow direction="long" className="shrink-0" />
                    <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#06E363]">
                      Long Win
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <TradeReviewChartLegendArrow direction="long" className="shrink-0" />
                    <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#E00101]">
                      Long Loss
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <TradeReviewChartLegendArrow direction="short" className="shrink-0" />
                    <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#06E363]">
                      Short Win
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <TradeReviewChartLegendArrow direction="short" className="shrink-0" />
                    <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-[#E00101]">
                      Short Loss
                    </span>
                  </span>
                </>
              )}
              </div>
            )}
            {regimeOverlayActive && !chartOhlcBlocked ? (
              <div
                data-trade-review-regime-legend=""
                aria-label="Regime overlay legend"
                className={
                  chartShellPending
                    ? "flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2"
                    : "flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-gray-200 pt-2.5 dark:border-sidebar-border"
                }
              >
                <span className="font-[family-name:var(--font-primary)] text-xs font-semibold leading-none text-[color:var(--brand-tropical-surf)]">
                  Regime:
                </span>
                {REGIME_OVERLAY_LEGEND_ITEMS.map((item) => (
                  <span key={item.value} className="inline-flex items-center gap-1.5">
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: item.solid }}
                      aria-hidden
                    />
                    <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-none text-gray-500 dark:text-sidebar-muted-foreground">
                      {item.label}
                    </span>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </Box>
      </Box>

      {collapsibleTradesList ? (
        <div data-trade-review-trades-accordion="" className="w-full min-w-0">
          <Accordion
            type="single"
            collapsible
            value={tradesListOpen ? "trades" : ""}
            onValueChange={(value) => setTradesListOpen(value === "trades")}
            className="space-y-0 border-0"
          >
            <AccordionItem
              value="trades"
              data-trade-review-trades-item=""
              className="border-border rounded-lg border px-3 not-last:border-b"
            >
              <AccordionTrigger
                className="items-center py-3 font-medium hover:no-underline"
                aria-label={tradesListOpen ? "Collapse trades list" : "Expand trades list"}
              >
                <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
                  {tradesListTitle}
                </span>
              </AccordionTrigger>
              <AccordionContent className="h-auto pt-0 pb-3 text-muted-foreground">
                <ExecutionTradesList
                  trades={tradesForList}
                  cohortPreviewMode={cohortPreviewMode}
                  selectedTradeId={selectedTradeId}
                  scrollToTradeId={scrollToTradeId}
                  onScrollHandled={handleScrollToTradeHandled}
                  onSelectTrade={handleSelectTradeFromList}
                  onOpenTrade={onOpenTrade}
                  showEditColumn={showEditColumn}
                  showPolicyColumn={useKeptRemovedPolicyTags}
                  showSimulationChangeColumn={showSimulationChangeColumn}
                  visualStyle={visualStyle}
                  showDustLabels={includeDust}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      ) : (
        <Box>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            fontFamily="'Urbanist', sans-serif"
            color={secondaryTextColor}
            mb={1}
          >
            {tradesListTitle}
          </Text>
          <ExecutionTradesList
            trades={tradesForList}
            cohortPreviewMode={cohortPreviewMode}
            selectedTradeId={selectedTradeId}
            scrollToTradeId={scrollToTradeId}
            onScrollHandled={handleScrollToTradeHandled}
            onSelectTrade={handleSelectTradeFromList}
            onOpenTrade={onOpenTrade}
            showEditColumn={showEditColumn}
            showPolicyColumn={useKeptRemovedPolicyTags}
            showSimulationChangeColumn={showSimulationChangeColumn}
            visualStyle={visualStyle}
            showDustLabels={includeDust}
          />
        </Box>
      )}
    </Box>
    </>
  );
};

export default TradeReviewTab;
